<template>
  <div class="media-detail-view">
    <div v-if="!data" class="no-data">
      データがありません
    </div>
    
    <div v-else>
      <!-- 媒体情報ヘッダー -->
      <div class="media-header">
        <h2>{{ data.media_name }}</h2>
        <div class="media-stats">
          <div class="stat-item">
            <span class="label">応募数:</span>
            <span class="value">{{ data.application_count }}人</span>
          </div>
          <div class="stat-item">
            <span class="label">面接実施数:</span>
            <span class="value">{{ data.interview_count }}人</span>
          </div>
          <div class="stat-item">
            <span class="label">採用数:</span>
            <span class="value">{{ data.hired_count }}人</span>
          </div>
        </div>
      </div>

      <!-- プロセス分析 -->
      <div class="process-analysis">
        <h3>採用プロセス分析</h3>
        <div class="process-flow">
          <div class="process-step">
            <div class="step-label">応募</div>
            <div class="step-count">{{ data.application_count }}人</div>
            <div class="step-rate">100%</div>
          </div>
          
          <div class="process-arrow">
            <i class="fas fa-arrow-right"></i>
            <div class="dropout-info">
              {{ data.application_count - data.interview_count }}人離脱
            </div>
          </div>
          
          <div class="process-step">
            <div class="step-label">面接実施</div>
            <div class="step-count">{{ data.interview_count }}人</div>
            <div class="step-rate">{{ interviewRate }}%</div>
          </div>
          
          <div class="process-arrow">
            <i class="fas fa-arrow-right"></i>
            <div class="dropout-info">
              {{ data.interview_count - data.hired_count }}人離脱
            </div>
          </div>
          
          <div class="process-step">
            <div class="step-label">採用</div>
            <div class="step-count">{{ data.hired_count }}人</div>
            <div class="step-rate">{{ hiredRate }}%</div>
          </div>
        </div>
      </div>

      <!-- 最大離脱ポイント詳細 -->
      <div class="dropout-detail">
        <h3>最大離脱ポイント</h3>
        <div class="dropout-card-detail">
          <div class="dropout-stage">{{ data.max_dropout.stage }}</div>
          <div class="dropout-count">{{ data.max_dropout.count }}人が離脱</div>
          <div class="dropout-rate">
            離脱率: {{ dropoutRate }}%
          </div>
        </div>
      </div>

      <!-- 改善提案 -->
      <div class="improvement-suggestions">
        <h3>改善提案</h3>
        <ul>
          <li v-for="suggestion in improvementSuggestions" :key="suggestion">
            {{ suggestion }}
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MediaDetailView',
  props: {
    mediaType: {
      type: String,
      required: true
    },
    data: {
      type: Object,
      default: null
    }
  },
  computed: {
    interviewRate() {
      if (!this.data || this.data.application_count === 0) return '0.0'
      return ((this.data.interview_count / this.data.application_count) * 100).toFixed(1)
    },
    
    hiredRate() {
      if (!this.data || this.data.application_count === 0) return '0.0'
      return ((this.data.hired_count / this.data.application_count) * 100).toFixed(1)
    },
    
    dropoutRate() {
      if (!this.data || this.data.application_count === 0) return '0.0'
      return ((this.data.max_dropout.count / this.data.application_count) * 100).toFixed(1)
    },
    
    improvementSuggestions() {
      const suggestions = []
      
      if (this.data) {
        // 面接実施率が低い場合
        if (parseFloat(this.interviewRate) < 30) {
          suggestions.push('応募者への初期対応を改善し、面接実施率を向上させることを検討してください')
        }
        
        // 採用率が低い場合
        if (parseFloat(this.hiredRate) < 10) {
          suggestions.push('求人要件と応募者のマッチング精度を見直すことをお勧めします')
        }
        
        // 応募→面接の離脱が多い場合
        if (this.data.max_dropout.stage === '応募→面接') {
          suggestions.push('応募後のスクリーニングプロセスや初期コミュニケーションを見直してください')
        }
        
        // 面接→採用の離脱が多い場合
        if (this.data.max_dropout.stage === '面接→採用') {
          suggestions.push('面接プロセスや選考基準の見直しを検討してください')
        }
      }
      
      return suggestions.length > 0 ? suggestions : ['現在、特に改善が必要な点は見つかりませんでした']
    }
  }
}
</script>

<style scoped>
.media-detail-view {
  padding: 20px;
}

.no-data {
  text-align: center;
  color: #666;
  padding: 40px;
}

.media-header {
  background-color: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 30px;
}

.media-header h2 {
  margin: 0 0 16px 0;
  color: #333;
}

.media-stats {
  display: flex;
  gap: 30px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.stat-item .label {
  color: #666;
}

.stat-item .value {
  font-weight: bold;
  font-size: 18px;
  color: #333;
}

.process-analysis {
  margin-bottom: 30px;
}

.process-analysis h3,
.dropout-detail h3,
.improvement-suggestions h3 {
  font-size: 18px;
  margin-bottom: 16px;
  color: #333;
}

.process-flow {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 30px;
  background-color: #f8f9fa;
  border-radius: 8px;
}

.process-step {
  background-color: white;
  border: 2px solid #007bff;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  min-width: 120px;
}

.step-label {
  font-weight: bold;
  color: #333;
  margin-bottom: 8px;
}

.step-count {
  font-size: 24px;
  font-weight: bold;
  color: #007bff;
  margin-bottom: 4px;
}

.step-rate {
  color: #666;
  font-size: 14px;
}

.process-arrow {
  margin: 0 20px;
  position: relative;
  color: #666;
}

.process-arrow i {
  font-size: 24px;
}

.dropout-info {
  position: absolute;
  top: -20px;
  left: 50%;
  transform: translateX(-50%);
  white-space: nowrap;
  font-size: 12px;
  color: #dc3545;
}

.dropout-detail {
  margin-bottom: 30px;
}

.dropout-card-detail {
  background-color: #fff3cd;
  border: 1px solid #ffeeba;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
}

.dropout-stage {
  font-size: 18px;
  font-weight: bold;
  color: #856404;
  margin-bottom: 8px;
}

.dropout-count {
  font-size: 24px;
  font-weight: bold;
  color: #d39e00;
  margin-bottom: 8px;
}

.dropout-rate {
  color: #856404;
}

.improvement-suggestions {
  background-color: #e7f3ff;
  border: 1px solid #b8daff;
  border-radius: 8px;
  padding: 20px;
}

.improvement-suggestions ul {
  margin: 0;
  padding-left: 20px;
}

.improvement-suggestions li {
  color: #004085;
  margin-bottom: 8px;
  line-height: 1.6;
}

.improvement-suggestions li:last-child {
  margin-bottom: 0;
}
</style>


// WEBPACK FOOTER //
// src/views/DashBoard/Report/MediaAnalysis/MediaDetailView.vue